import { Box, Button, Container, Heading, Image, Link, Text, VStack } from '@chakra-ui/react'
import { LanguageSwitch } from 'commons/components/layout/LanguageSwitch'
import PageLayout from 'commons/components/layout/PageLayout'
import {
	BgColors,
	BorderRadius,
	BrandColors,
	FontFamily,
	Spacing,
	TextColors
} from 'commons/design-system/tokens'
import { ClaimTagFooter } from 'features/claim-tag/components/ClaimTagFooter'
import { HeroIllustration } from 'features/claim-tag/components/HeroIllustration'
import { InfoCard } from 'features/claim-tag/components/InfoCard'
import {
	buildClaimTagDeeplink,
	buildLydiaAppUrl,
	LYDIA_APP_DOWNLOAD_URL
} from 'features/claim-tag/utils/buildClaimTagDeeplink'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const ClaimTagPage = () => {
	const { t } = useTranslation()

	const tag = useMemo(() => {
		const urlParams = new URLSearchParams(window.location.search)
		return urlParams.get('t') || ''
	}, [])

	const handleActivateClick = () => {
		if (tag) {
			window.location.href = buildClaimTagDeeplink(tag)
		} else {
			window.location.href = buildLydiaAppUrl()
		}
	}

	return (
		<Box minH='100vh' w='full' bg={BgColors.HIGHLIGHT_Z2}>
			<PageLayout alignItems='center' footer={<ClaimTagFooter />} mb={0} pb={0} mt={0}>
				<Container
					maxW='600px'
					px={Spacing.SM}
					pt={{ base: 6, md: 12 }}
					pb={{ base: 4, md: 6 }}
					centerContent
				>
					<VStack gap={4} w='full' textAlign='center'>
						<Image
							src={`${process.env.REACT_APP_PAYMENT_FORM_URL}l/assets/img/logos/lydia-logo-v3.png`}
							alt='Lydia'
							w='103px'
							h='auto'
						/>
						<VStack gap={0}>
							<Heading
								as='h1'
								fontFamily={FontFamily.DEGULAR}
								fontWeight={700}
								fontSize={{ base: '28px', md: '36px' }}
								lineHeight='1.2'
								color={TextColors.PRIMARY}
								mb={2}
							>
								{t('claim_tag.title')}
							</Heading>
							<Text fontSize='16px' lineHeight='24px' color={TextColors.PRIMARY}>
								{t('claim_tag.subtitle_1')}
							</Text>
							<Text fontSize='16px' lineHeight='24px' color={TextColors.PRIMARY}>
								{t('claim_tag.subtitle_2')}
							</Text>
						</VStack>
						<HeroIllustration />
						<VStack gap={3} w='full' maxW='400px'>
							<Button
								w='full'
								h='52px'
								borderRadius={BorderRadius.XL}
								bg={BrandColors.PRIMARY}
								color={TextColors.OPPOSITE_PRIMARY}
								fontFamily={FontFamily.MONTSERRAT}
								fontWeight={700}
								fontSize='16px'
								onClick={handleActivateClick}
								_hover={{ bg: BrandColors.PRIMARY, opacity: 0.9 }}
							>
								{t('claim_tag.cta')}
							</Button>
							<Text fontSize='13px' color={TextColors.QUATERNARY} fontWeight={600}>
								{t('claim_tag.cta_disclaimer')}
							</Text>
						</VStack>
						<VStack gap={4} w='full' mt={4}>
							<InfoCard title={t('claim_tag.new_customer_title')}>
								<VStack
									as='ol'
									align='stretch'
									gap={3}
									pl={5}
									fontSize='15px'
									lineHeight='22px'
									color={TextColors.SECONDARY}
									textAlign='left'
									listStyleType='decimal'
								>
									<Box as='li'>
										<Link
											href={LYDIA_APP_DOWNLOAD_URL}
											target='_blank'
											rel='noopener noreferrer'
											textDecoration='underline'
										>
											{t('claim_tag.new_customer_step_1')}
										</Link>
									</Box>
									<Box as='li'>{t('claim_tag.new_customer_step_2')}</Box>
									<Box as='li'>{t('claim_tag.new_customer_step_3')}</Box>
								</VStack>
							</InfoCard>
							<InfoCard title={t('claim_tag.fees_title')}>
								<VStack
									as='ul'
									align='stretch'
									gap={3}
									pl={5}
									fontSize='15px'
									lineHeight='22px'
									color={TextColors.SECONDARY}
									textAlign='left'
									listStyleType='disc'
								>
									<Box as='li'>
										<Trans
											i18nKey='claim_tag.fees_free_activation'
											components={{ bold: <Text as='span' fontWeight={700} /> }}
										/>
									</Box>
									<Box as='li'>
										<Trans
											i18nKey='claim_tag.fees_free_lydia'
											components={{ bold: <Text as='span' fontWeight={700} /> }}
										/>
									</Box>
									<Box as='li'>
										<Trans
											i18nKey='claim_tag.fees_commission'
											components={{ bold: <Text as='span' fontWeight={700} /> }}
										/>
									</Box>
								</VStack>
							</InfoCard>
						</VStack>
						<LanguageSwitch />
					</VStack>
				</Container>
			</PageLayout>
		</Box>
	)
}

export default ClaimTagPage
